/**
 * RFIA (Rules File Integrity Analysis) Section Component
 *
 * Displays RFIA scan results in the security report — rules file integrity
 * analysis findings with traffic light scoring, severity breakdown, and
 * individual finding details.
 *
 * @copyright 2026 Guardrail Security Technologies
 */

import { Shield, AlertTriangle, CheckCircle, XCircle, FileWarning, Eye } from 'lucide-react';
import { useState } from 'react';
import { cn } from '@/lib/utils';
import type { RFIAFindingData, RFIAAggregateData } from '@/utils/scanToInsights';

// ─── Types ──────────────────────────────────────────────────────────────────

interface RFIASectionProps {
  findings: RFIAFindingData[];
  aggregate: RFIAAggregateData;
  /** When true, all finding cards start expanded and all findings are shown (print mode) */
  defaultExpanded?: boolean;
}

// ─── Traffic Light Colors ───────────────────────────────────────────────────

const TRAFFIC_LIGHT_CONFIG: Record<string, {
  label: string;
  color: string;
  bgColor: string;
  borderColor: string;
  icon: typeof CheckCircle;
  description: string;
}> = {
  GREEN: {
    label: 'GREEN',
    color: 'text-emerald-400',
    bgColor: 'bg-emerald-500/10',
    borderColor: 'border-emerald-500/20',
    icon: CheckCircle,
    description: 'No integrity violations detected in rules files.',
  },
  AMBER: {
    label: 'AMBER',
    color: 'text-amber-400',
    bgColor: 'bg-amber-500/10',
    borderColor: 'border-amber-500/20',
    icon: AlertTriangle,
    description: 'Suspicious patterns detected in rules files. Review recommended.',
  },
  RED: {
    label: 'RED',
    color: 'text-red-400',
    bgColor: 'bg-red-500/10',
    borderColor: 'border-red-500/20',
    icon: XCircle,
    description: 'Critical integrity violations detected. Rules files may be compromised.',
  },
};

// ─── Category Labels ────────────────────────────────────────────────────────

// Keys match the RFIAFindingCategory enum values from @guardrail/rfia/types
const CATEGORY_LABELS: Record<string, { label: string; description: string }> = {
  unicode_steganography: {
    label: 'Unicode Steganography',
    description: 'Invisible Unicode characters that may hide malicious content',
  },
  behavioral_directive: {
    label: 'Behavioral Directive',
    description: 'Suspicious or malicious instructions to AI coding assistants',
  },
  provenance_tamper: {
    label: 'Provenance Tamper',
    description: 'File content has changed from known baseline (possible tampering)',
  },
  cross_session_persistence: {
    label: 'Cross-Session Persistence',
    description: 'Rules that persist across sessions or affect global scope',
  },
  supply_chain: {
    label: 'Supply Chain',
    description: 'Rules file supply chain integrity concern',
  },
  polyglot_format: {
    label: 'Polyglot Format',
    description: 'File valid in multiple formats (possible format confusion attack)',
  },
};

// ─── Severity Config ────────────────────────────────────────────────────────

const SEVERITY_CONFIG: Record<string, { color: string; bgColor: string; label: string }> = {
  critical: { color: 'text-red-400', bgColor: 'bg-red-500/10', label: 'Critical' },
  high: { color: 'text-orange-400', bgColor: 'bg-orange-500/10', label: 'High' },
  medium: { color: 'text-amber-400', bgColor: 'bg-amber-500/10', label: 'Medium' },
  low: { color: 'text-emerald-400', bgColor: 'bg-emerald-500/10', label: 'Low' },
  info: { color: 'text-sky-400', bgColor: 'bg-sky-500/10', label: 'Info' },
};

// ─── Sub-Components ─────────────────────────────────────────────────────────

/** Traffic light score badge */
function TrafficLightBadge({ score }: { score: string }) {
  const config = TRAFFIC_LIGHT_CONFIG[score] || TRAFFIC_LIGHT_CONFIG.AMBER;
  const Icon = config.icon;

  return (
    <div className={cn('flex items-center gap-2 px-4 py-2 rounded-lg border', config.bgColor, config.borderColor)}>
      <Icon className={cn('w-5 h-5', config.color)} />
      <span className={cn('font-bold text-lg', config.color)}>{config.label}</span>
    </div>
  );
}

/** Severity breakdown bar */
function SeverityBar({ bySeverity }: { bySeverity: RFIAAggregateData['bySeverity'] }) {
  const total = bySeverity.critical + bySeverity.high + bySeverity.medium + bySeverity.low + bySeverity.info;
  if (total === 0) return null;

  const segments = [
    { key: 'critical', count: bySeverity.critical, color: 'bg-red-600' },
    { key: 'high', count: bySeverity.high, color: 'bg-orange-500' },
    { key: 'medium', count: bySeverity.medium, color: 'bg-amber-500' },
    { key: 'low', count: bySeverity.low, color: 'bg-emerald-500' },
    { key: 'info', count: bySeverity.info, color: 'bg-sky-500' },
  ].filter((s) => s.count > 0);

  return (
    <div className="space-y-2">
      {/* Stacked bar */}
      <div className="flex h-3 rounded-full overflow-hidden bg-zinc-800">
        {segments.map((seg) => (
          <div
            key={seg.key}
            className={cn('transition-all', seg.color)}
            style={{ width: `${(seg.count / total) * 100}%` }}
            title={`${SEVERITY_CONFIG[seg.key]?.label}: ${seg.count}`}
          />
        ))}
      </div>
      {/* Legend */}
      <div className="flex flex-wrap gap-3 text-xs text-zinc-400">
        {segments.map((seg) => {
          const config = SEVERITY_CONFIG[seg.key];
          return (
            <div key={seg.key} className="flex items-center gap-1.5">
              <div className={cn('w-2.5 h-2.5 rounded-sm', seg.color)} />
              <span>{config?.label}: {seg.count}</span>
            </div>
          );
        })}
      </div>
    </div>
  );
}

/** Individual RFIA finding card */
function RFIAFindingCard({ finding, defaultExpanded = false }: { finding: RFIAFindingData; defaultExpanded?: boolean }) {
  const [expanded, setExpanded] = useState(defaultExpanded);
  const sevConfig = SEVERITY_CONFIG[finding.severity] || SEVERITY_CONFIG.info;
  const catInfo = CATEGORY_LABELS[finding.category];

  return (
    <div className="border border-zinc-700/50 rounded-lg overflow-hidden bg-zinc-900/30">
      <button
        onClick={() => setExpanded(!expanded)}
        className="w-full flex items-center gap-3 p-3 hover:bg-zinc-800/30 transition-colors text-left"
      >
        <div className={cn('px-2 py-0.5 rounded text-[10px] font-bold uppercase', sevConfig.bgColor, sevConfig.color)}>
          {sevConfig.label}
        </div>
        <span className="flex-1 text-sm font-medium text-zinc-200 truncate">
          {finding.title}
        </span>
        {catInfo && (
          <span className="text-[10px] text-zinc-500 hidden sm:inline">
            {catInfo.label}
          </span>
        )}
        {finding.location?.file && (
          <span className="text-[10px] text-zinc-500 font-mono hidden md:inline truncate max-w-[200px]">
            {finding.location.file}{finding.location.line ? `:${finding.location.line}` : ''}
          </span>
        )}
        <Eye className={cn('w-4 h-4 transition-transform', expanded ? 'text-zinc-300' : 'text-zinc-600')} />
      </button>

      {expanded && (
        <div className="px-4 pb-4 border-t border-zinc-800 bg-zinc-900/50 animate-fade-in">
          <div className="pt-3 space-y-3">
            <p className="text-sm text-zinc-300">{finding.description}</p>

            {finding.location?.file && (
              <div className="flex items-center gap-2 text-xs text-zinc-400 bg-zinc-800/50 px-3 py-2 rounded-md font-mono">
                <FileWarning className="w-3.5 h-3.5 flex-shrink-0" />
                <span>{finding.location.file}{finding.location.line ? `:${finding.location.line}` : ''}</span>
              </div>
            )}

            {finding.remediation && (
              <div>
                <h5 className="text-xs font-semibold text-zinc-500 uppercase tracking-wider mb-1">Remediation</h5>
                <p className="text-sm text-emerald-300/80 bg-emerald-500/5 border border-emerald-500/10 p-2.5 rounded-md">
                  {finding.remediation}
                </p>
              </div>
            )}

            {finding.cwe && (
              <div className="flex items-center gap-2 pt-1">
                <span className="text-[10px] text-zinc-500">Reference:</span>
                <a
                  href={`https://cwe.mitre.org/data/definitions/${finding.cwe.replace('CWE-', '')}.html`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-[10px] text-cyan-400 hover:underline"
                >
                  {finding.cwe}
                </a>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

// ─── Main Component ─────────────────────────────────────────────────────────

const RFIASection = ({ findings, aggregate, defaultExpanded = false }: RFIASectionProps) => {
  const [showAll, setShowAll] = useState(defaultExpanded);
  const INITIAL_DISPLAY = 10;

  const displayedFindings = showAll || defaultExpanded ? findings : findings.slice(0, INITIAL_DISPLAY);
  const hasMore = findings.length > INITIAL_DISPLAY;

  const tlConfig = TRAFFIC_LIGHT_CONFIG[aggregate.worstScore] || TRAFFIC_LIGHT_CONFIG.AMBER;

  return (
    <div className="space-y-6">
      {/* Overview Card */}
      <div className={cn('rounded-xl border p-6', tlConfig.bgColor, tlConfig.borderColor)}>
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 mb-4">
          <div className="flex items-center gap-3">
            <Shield className={cn('w-6 h-6', tlConfig.color)} />
            <div>
              <h3 className="text-lg font-semibold text-white">Rules File Integrity</h3>
              <p className="text-sm text-zinc-400">{tlConfig.description}</p>
            </div>
          </div>
          <TrafficLightBadge score={aggregate.worstScore} />
        </div>

        {/* Stats grid */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mt-4">
          <div className="bg-zinc-900/40 rounded-lg p-3 text-center">
            <div className="text-2xl font-bold text-white">{aggregate.filesScanned}</div>
            <div className="text-xs text-zinc-500 mt-1">Files Scanned</div>
          </div>
          <div className="bg-zinc-900/40 rounded-lg p-3 text-center">
            <div className="text-2xl font-bold text-white">{aggregate.totalFindings}</div>
            <div className="text-xs text-zinc-500 mt-1">Findings</div>
          </div>
          <div className="bg-zinc-900/40 rounded-lg p-3 text-center">
            <div className="text-2xl font-bold text-white">
              {aggregate.totalBytes < 1024
                ? `${aggregate.totalBytes} B`
                : `${(aggregate.totalBytes / 1024).toFixed(1)} KB`}
            </div>
            <div className="text-xs text-zinc-500 mt-1">Analyzed</div>
          </div>
          <div className="bg-zinc-900/40 rounded-lg p-3 text-center">
            <div className="text-2xl font-bold text-white">{aggregate.totalDurationMs} ms</div>
            <div className="text-xs text-zinc-500 mt-1">Scan Time</div>
          </div>
        </div>

        {/* Severity breakdown */}
        {aggregate.totalFindings > 0 && (
          <div className="mt-4">
            <SeverityBar bySeverity={aggregate.bySeverity} />
          </div>
        )}
      </div>

      {/* RED override warning */}
      {aggregate.worstScore === 'RED' && (
        <div className="flex items-start gap-3 p-4 bg-red-500/10 border border-red-500/20 rounded-lg">
          <XCircle className="w-5 h-5 text-red-400 shrink-0 mt-0.5" />
          <div>
            <h4 className="font-medium text-red-400">RFIA RED Override Active</h4>
            <p className="text-sm text-zinc-400 mt-1">
              Critical integrity violations detected in AI rules files. The overall scan recommendation
              has been overridden to <strong className="text-red-300">DO NOT INSTALL</strong>. The
              rules files may contain hidden instructions, steganographic content, or evidence of
              supply chain tampering that could compromise AI coding assistant behavior.
            </p>
          </div>
        </div>
      )}

      {/* Findings list */}
      {findings.length > 0 && (
        <div className="space-y-2">
          <div className="flex items-center justify-between mb-2">
            <h4 className="text-sm font-semibold text-zinc-400 uppercase tracking-wider">
              Findings ({findings.length})
            </h4>
          </div>

          <div className="space-y-2">
            {displayedFindings.map((finding) => (
              <RFIAFindingCard key={finding.id} finding={finding} defaultExpanded={defaultExpanded} />
            ))}
          </div>

          {hasMore && !showAll && (
            <button
              onClick={() => setShowAll(true)}
              className="w-full py-2 text-sm text-cyan-400 hover:text-cyan-300 transition-colors border border-zinc-700/50 rounded-lg hover:bg-zinc-800/30"
            >
              Show all {findings.length} findings
            </button>
          )}
        </div>
      )}

      {/* Clean state */}
      {findings.length === 0 && aggregate.worstScore === 'GREEN' && (
        <div className="flex items-center gap-3 p-4 bg-emerald-500/5 border border-emerald-500/10 rounded-lg">
          <CheckCircle className="w-5 h-5 text-emerald-400" />
          <p className="text-sm text-zinc-300">
            All {aggregate.filesScanned} rules file{aggregate.filesScanned !== 1 ? 's' : ''} passed
            integrity analysis with no findings.
          </p>
        </div>
      )}
    </div>
  );
};

export default RFIASection;
